import useComplexState from '@/lib/utils/useComplexState';
import { type OneDriveFile, type OneDriveFolder } from './types';

type OneDriveRootFolderState = {
  folderName: null;
  isRootFolder: true;
  items: (OneDriveFile | OneDriveFolder)[];
};

type OneDriveFolderState = {
  folderName: string;
  isRootFolder: false;
  items: (OneDriveFile | OneDriveFolder)[];
};

type OneDriveFilesState = [
  OneDriveRootFolderState | null,
  ...OneDriveFolderState[],
];

const useOneDriveFilesState = (): {
  activeFolder: OneDriveRootFolderState | OneDriveFolderState | null;
  openFolder: (
    folderName: string,
    folderItems: (OneDriveFile | OneDriveFolder)[],
  ) => void;
  closeFolder: (folderIndex: number) => void;
  folderBreadcrumbs: [number, string][];
  setRootFiles: (files: (OneDriveFile | OneDriveFolder)[]) => void;
  resetFilesState: () => void;
} => {
  const [oneDriveFiles, setOneDriveFiles] = useComplexState<OneDriveFilesState>(
    [null],
  );

  const setRootFiles = (files: (OneDriveFile | OneDriveFolder)[]): void => {
    setOneDriveFiles(oneDriveFiles => {
      oneDriveFiles[0] = {
        folderName: null,
        isRootFolder: true,
        items: files,
      };
    });
  };

  const openFolder = (
    folderName: string,
    folderItems: (OneDriveFile | OneDriveFolder)[],
  ): void => {
    setOneDriveFiles(oneDriveFiles => {
      oneDriveFiles.push({
        folderName,
        isRootFolder: false,
        items: folderItems,
      });
    });
  };

  const folderBreadcrumbs = (
    oneDriveFiles.slice(1) as OneDriveFolderState[]
  ).map((folder, index) => [index + 1, folder.folderName] as [number, string]);

  const activeFolder = oneDriveFiles[oneDriveFiles.length - 1];

  const closeFolder = (folderStateIndex: number): void => {
    if (oneDriveFiles.length === 1 || folderStateIndex === 0) {
      return;
    }

    setOneDriveFiles(oneDriveFiles => oneDriveFiles.slice(0, folderStateIndex));
  };

  const resetFilesState = (): void => {
    setOneDriveFiles([null]);
  };

  return {
    activeFolder,
    openFolder,
    closeFolder,
    folderBreadcrumbs,
    setRootFiles,
    resetFilesState,
  };
};

export default useOneDriveFilesState;
